import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client/react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  IconAlertCircle,
  IconCheck,
  IconDeviceFloppy,
  IconDots,
  IconGripVertical,
  IconRefresh,
  IconUserOff,
} from "@tabler/icons-react";
import {
  UPDATE_SEEDING_ORDER_MUTATION,
  WITHDRAW_TEAM_FROM_STEP_MUTATION,
} from "./seeding.operations";
import type { SeedingTeam } from "./seeding.types";

interface SeedingOrderTabProps {
  readonly stepId: string;
  readonly teams: readonly SeedingTeam[];
  readonly seedingOrder: readonly string[];
  readonly onOrderSaved?: (orderedTeamIds: readonly string[]) => void;
  readonly onTeamWithdrawn?: (teamId: string) => void;
  readonly canWithdraw?: boolean;
}

const orderTeams = (
  teams: readonly SeedingTeam[],
  order: readonly string[],
): SeedingTeam[] => {
  const byId = new Map(teams.map((t) => [t.id, t]));
  const ordered: SeedingTeam[] = [];
  for (const id of order) {
    const team = byId.get(id);
    if (team) {
      ordered.push(team);
      byId.delete(id);
    }
  }
  // Append any teams not present in the order (newly added teams).
  for (const team of byId.values()) ordered.push(team);
  return ordered;
};

export const SeedingOrderTab = ({
  stepId,
  teams,
  seedingOrder,
  onOrderSaved,
  onTeamWithdrawn,
  canWithdraw = true,
}: SeedingOrderTabProps) => {
  const initialOrder = useMemo(
    () => orderTeams(teams, seedingOrder),
    [teams, seedingOrder],
  );
  const [localOrder, setLocalOrder] = useState<SeedingTeam[]>(initialOrder);

  // Reset local state if the incoming teams / order changes materially.
  useEffect(() => {
    setLocalOrder(initialOrder);
  }, [initialOrder]);

  const [updateSeedingOrder, { loading: saving }] = useMutation(
    UPDATE_SEEDING_ORDER_MUTATION,
  );
  const [withdrawTeam, { loading: withdrawing }] = useMutation(
    WITHDRAW_TEAM_FROM_STEP_MUTATION,
  );

  const hasChanges = useMemo(() => {
    if (localOrder.length !== initialOrder.length) return true;
    return localOrder.some((t, idx) => t.id !== initialOrder[idx]?.id);
  }, [localOrder, initialOrder]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const sourceIdx = result.source.index;
    const destIdx = result.destination.index;
    if (sourceIdx === destIdx) return;
    const next = Array.from(localOrder);
    const [moved] = next.splice(sourceIdx, 1);
    next.splice(destIdx, 0, moved);
    setLocalOrder(next);
  };

  const handleSave = async () => {
    try {
      const orderedTeamIds = localOrder.map((t) => t.id);
      const result = await updateSeedingOrder({
        variables: { stepId, orderedTeamIds },
      });
      if (result?.error) {
        throw new Error(result.error.message);
      }
      notifications.show({
        title: "Seeding order saved",
        message: "The new seeding order has been persisted.",
        color: "green",
        icon: <IconCheck size={16} />,
      });
      onOrderSaved?.(orderedTeamIds);
    } catch (err) {
      notifications.show({
        title: "Failed to save seeding order",
        message: err instanceof Error ? err.message : "Unexpected error",
        color: "red",
      });
    }
  };

  const handleWithdraw = (team: SeedingTeam) => {
    modals.openConfirmModal({
      title: `Withdraw ${team.name}?`,
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            This will flip the team status to WITHDRAWN and fire the{" "}
            <Text component="span" fw={600}>
              TEAM_WITHDREW
            </Text>{" "}
            rule-engine event. If the step is already seeded, the preset&apos;s
            withdrawal rule will trigger a re-seed or cascade bye resolution.
          </Text>
          <Alert
            color="yellow"
            icon={<IconAlertCircle size={16} />}
            variant="light"
          >
            Idempotent — can be safely retried.
          </Alert>
        </Stack>
      ),
      labels: { confirm: "Withdraw", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          const result = await withdrawTeam({
            variables: { stepId, teamId: team.id },
          });
          if (result?.error) {
            throw new Error(result.error.message);
          }
          notifications.show({
            title: "Team withdrawn",
            message: `${team.name} has been withdrawn from this step.`,
            color: "green",
            icon: <IconCheck size={16} />,
          });
          onTeamWithdrawn?.(team.id);
        } catch (err) {
          notifications.show({
            title: "Withdraw failed",
            message: err instanceof Error ? err.message : "Unexpected error",
            color: "red",
          });
        }
      },
    });
  };

  if (localOrder.length === 0) {
    return (
      <Stack align="center" py="xl" gap="sm">
        <Loader color="cyan" size="sm" />
        <Text size="sm" c="dimmed">
          No teams available for seeding.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Text fw={600}>Seeding order</Text>
          <Text size="xs" c="dimmed">
            Drag to reorder. The SEEDING rules of the preset will consume this
            order when seeding the step.
          </Text>
        </Stack>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          disabled={!hasChanges || saving}
          loading={saving}
          onClick={handleSave}
        >
          Save order
        </Button>
      </Group>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="seeding-order">
          {(provided) => (
            <Stack
              gap="xs"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {localOrder.map((team, index) => (
                <Draggable
                  key={team.id}
                  draggableId={team.id}
                  index={index}
                  isDragDisabled={saving || withdrawing}
                >
                  {(draggable, snapshot) => (
                    <Card
                      withBorder
                      padding="sm"
                      ref={draggable.innerRef}
                      {...draggable.draggableProps}
                      style={{
                        ...draggable.draggableProps.style,
                        backgroundColor: snapshot.isDragging
                          ? "var(--mantine-color-dark-5)"
                          : undefined,
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap">
                          <div
                            {...draggable.dragHandleProps}
                            aria-label={`Drag to reorder ${team.name}`}
                            style={{ cursor: "grab", display: "flex" }}
                          >
                            <IconGripVertical size={18} />
                          </div>
                          <Badge variant="light" color="cyan">
                            #{index + 1}
                          </Badge>
                          <Text fw={500}>{team.name}</Text>
                        </Group>
                        {canWithdraw && (
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                aria-label={`More actions for ${team.name}`}
                              >
                                <IconDots size={14} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                color="red"
                                leftSection={<IconUserOff size={14} />}
                                onClick={() => handleWithdraw(team)}
                              >
                                Withdraw from step
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        )}
                      </Group>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      {hasChanges && (
        <Alert
          color="blue"
          icon={<IconRefresh size={16} />}
          variant="light"
        >
          Unsaved changes. Click &ldquo;Save order&rdquo; to persist the new
          ordering. Saving does not re-seed the step on its own — use
          &ldquo;Re-seed step&rdquo; on the step dashboard when the step is in
          SEEDED status to apply this order to the bracket.
        </Alert>
      )}
    </Stack>
  );
};
